import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import type { ComplianceFrameworkDefDTO } from '@deepguide-ai/types'
import { Plus, Shield } from 'lucide-react'
import { toast } from 'sonner'
import { useDocumentTitle } from 'usehooks-ts'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

// Framework logo/icon mapping
const getFrameworkIcon = (_name: string) => {
  // For now, using Shield icon for all frameworks
  // Can be extended with actual logo images or specific icons
  return Shield
}

const getFrameworkColor = (name: string) => {
  const colors: Record<string, string> = {
    'soc 2': 'text-blue-500',
    'iso 27001': 'text-purple-500',
    hipaa: 'text-green-500',
    cmmc: 'text-orange-500',
  }
  return colors[name.toLowerCase()] || 'text-gray-500'
}

interface FrameworkCardProps {
  id: string
  name: string
  isCustom: boolean
  stats: {
    totalControls: number
    totalTests: number
    passed: number
    failed: number
    inReview: number
    notStarted: number
  }
  onClick: () => void
}

function FrameworkCard({
  id: _id,
  name,
  isCustom,
  stats,
  onClick,
}: FrameworkCardProps) {
  const Icon = getFrameworkIcon(name)
  const iconColor = getFrameworkColor(name)

  const passRate =
    stats.totalTests > 0
      ? Math.round((stats.passed / stats.totalTests) * 100)
      : 0

  return (
    <div className='group border-border/50 bg-card hover:border-primary/30 relative w-full rounded-xl border text-left shadow-xs transition-all hover:shadow-md'>
      {/* Clickable overlay */}
      <button
        onClick={onClick}
        className='absolute inset-0 z-0 cursor-pointer rounded-xl'
        aria-label={`View ${name}`}
      />

      {/* Header */}
      <div className='p-5 pb-4'>
        <div className='flex items-start gap-3'>
          <div className={`bg-muted/50 rounded-lg p-2.5 ${iconColor} shrink-0`}>
            <Icon className='h-5 w-5' strokeWidth={1.5} />
          </div>
          <div className='min-w-0 flex-1'>
            <div className='flex items-center gap-2'>
              <h3 className='truncate text-base font-semibold'>{name}</h3>
              {isCustom && (
                <Badge
                  variant='outline'
                  className='h-4 px-1.5 py-0 text-[10px] font-medium'
                >
                  Custom
                </Badge>
              )}
            </div>
            <p className='text-muted-foreground mt-0.5 text-xs'>
              {stats.totalControls} controls · {stats.totalTests} tests
            </p>
          </div>
        </div>
      </div>

      {/* Test Stats with Donut */}
      <div className='px-5 pb-5'>
        <div className='flex items-center gap-4'>
          {/* Mini Donut Chart */}
          <div
            className='relative flex-shrink-0'
            style={{ width: 56, height: 56 }}
          >
            <svg
              width='56'
              height='56'
              viewBox='0 0 56 56'
              className='-rotate-90 transform'
            >
              <circle
                cx='28'
                cy='28'
                r='22'
                fill='none'
                stroke='currentColor'
                strokeWidth='6'
                className='text-muted/20'
              />
              {stats.totalTests > 0 && stats.passed > 0 && (
                <circle
                  cx='28'
                  cy='28'
                  r='22'
                  fill='none'
                  stroke='rgb(34, 197, 94)'
                  strokeWidth='6'
                  strokeDasharray={`${(stats.passed / stats.totalTests) * 138.23} 138.23`}
                  strokeLinecap='round'
                  className='transition-all duration-500'
                />
              )}
            </svg>
            <div className='absolute inset-0 flex flex-col items-center justify-center'>
              <span className='text-xs font-semibold tabular-nums'>
                {passRate}%
              </span>
            </div>
          </div>

          {/* Stats Grid */}
          <div className='grid flex-1 grid-cols-4 gap-2'>
            <div className='text-center'>
              <div className='flex items-center justify-center gap-1'>
                <span className='h-1.5 w-1.5 rounded-full bg-green-500' />
                <span className='text-sm font-semibold text-green-600 tabular-nums dark:text-green-500'>
                  {stats.passed}
                </span>
              </div>
              <div className='text-muted-foreground text-[9px] tracking-wide uppercase'>
                Passed
              </div>
            </div>
            <div className='text-center'>
              <div className='flex items-center justify-center gap-1'>
                <span className='h-1.5 w-1.5 rounded-full bg-red-500' />
                <span className='text-sm font-semibold text-red-600 tabular-nums dark:text-red-500'>
                  {stats.failed}
                </span>
              </div>
              <div className='text-muted-foreground text-[9px] tracking-wide uppercase'>
                Failed
              </div>
            </div>
            <div className='text-center'>
              <div className='flex items-center justify-center gap-1'>
                <span className='h-1.5 w-1.5 rounded-full bg-amber-500' />
                <span className='text-sm font-semibold text-amber-600 tabular-nums dark:text-amber-500'>
                  {stats.inReview}
                </span>
              </div>
              <div className='text-muted-foreground text-[9px] tracking-wide uppercase'>
                In Review
              </div>
            </div>
            <div className='text-center'>
              <div className='flex items-center justify-center gap-1'>
                <span className='bg-muted-foreground/40 h-1.5 w-1.5 rounded-full' />
                <span className='text-muted-foreground text-sm font-semibold tabular-nums'>
                  {stats.notStarted}
                </span>
              </div>
              <div className='text-muted-foreground text-[9px] tracking-wide uppercase'>
                Not Started
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface FrameworkFormData {
  name: string
  slug: string
  description: string
}

function ComplianceHomePage() {
  useDocumentTitle('Compliance')

  const navigate = useNavigate()
  const { workspaceId } = Route.useParams()
  const queryClient = useQueryClient()
  const { role } = useWorkspacePermissions(workspaceId)
  const isViewer = role === 'VIEWER'

  // Dialog and form state for custom frameworks
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [formData, setFormData] = useState<FrameworkFormData>({
    name: '',
    slug: '',
    description: '',
  })

  // Fetch frameworks with counts
  const frameworksQuery = useQuery(
    trpc.guidedTemplate.listFrameworksWithCounts.queryOptions()
  )

  // Fetch controls tree for all frameworks to get statistics
  const treeQuery = useQuery(trpc.compliance.getControlsTree.queryOptions({}))
  const frameworks = (frameworksQuery.data ??
    []) as (ComplianceFrameworkDefDTO & {
    _count?: { controls: number; templates: number }
  })[]

  // Mutations
  const createFrameworkMutation = useMutation({
    ...trpc.guidedTemplate.createFramework.mutationOptions(),
    onSuccess: () => {
      toast.success('Framework created successfully')
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.listFrameworksWithCounts.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({}),
      })
      setIsCreateDialogOpen(false)
      resetForm()
    },
    onError: (error) => {
      toast.error('Failed to create framework')
      console.error('Failed to create framework:', error)
    },
  })

  // Handlers
  const resetForm = () => {
    setFormData({ name: '', slug: '', description: '' })
  }

  const handleCreateFramework = () => {
    if (!formData.name.trim()) {
      toast.error('Framework name is required')
      return
    }
    if (!formData.slug.trim()) {
      toast.error('Framework slug is required')
      return
    }
    createFrameworkMutation.mutate({
      name: formData.name,
    })
  }

  // Navigate to setup guide to add tests
  const handleAddTests = () => {
    navigate({
      to: '/workspaces/$workspaceId/compliance/setup',
      params: { workspaceId },
    })
  }

  // Calculate statistics for each framework from the tree (test-centric)
  const frameworkStats = useMemo(() => {
    const tree = treeQuery.data?.tree || []
    const stats: Record<
      string,
      {
        totalControls: number
        totalTests: number
        passed: number
        failed: number
        inReview: number
        notStarted: number
      }
    > = {}

    tree.forEach((frameworkNode) => {
      if (frameworkNode.type === 'framework') {
        let totalControls = 0
        let totalTests = 0
        let passed = 0
        let failed = 0
        let inReview = 0
        let notStarted = 0
        // 去重集合，避免同一个测试挂在多个控制下重复计数
        const seenTestIds = new Set<string>()

        // Iterate through categories
        frameworkNode.children?.forEach((category) => {
          // Iterate through controls
          category.children?.forEach((control) => {
            if (control.type === 'control') {
              totalControls++

              // Count tests and their statuses
              control.children?.forEach((test) => {
                if (test.type === 'test' && test.topicId) {
                  // 同 topicId 只统计一次
                  if (seenTestIds.has(test.topicId)) return
                  seenTestIds.add(test.topicId)

                  totalTests++
                  if (test.status === 'PASSED') passed++
                  else if (test.status === 'FAILED') failed++
                  else if (test.status === 'NEEDS_REVIEW') inReview++
                  else notStarted++ // NOT_TESTED or any other status
                }
              })
            }
          })
        })

        stats[frameworkNode.id] = {
          totalControls,
          totalTests,
          passed,
          failed,
          inReview,
          notStarted,
        }
      }
    })

    return stats
  }, [treeQuery.data?.tree])

  const handleFrameworkClick = (frameworkId: string) => {
    navigate({
      to: '/workspaces/$workspaceId/compliance/$frameworkId',
      params: { workspaceId, frameworkId },
    })
  }


  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 overflow-y-auto bg-muted p-8'>
        <div className='mx-auto flex max-w-[1200px] flex-col gap-8'>
          {/* Header section */}
          <div className='flex items-start justify-between'>
            <div className='flex flex-col gap-1'>
              <h1 className='font-[Fraunces] text-[32px] font-medium leading-tight tracking-tight text-sidebar-foreground'>
                Compliance Frameworks
              </h1>
              <p className='text-sm leading-relaxed text-muted-foreground'>
                Manage compliance controls across different frameworks and
                standards.
              </p>
            </div>
            {!isViewer && (
              <Button size='sm' onClick={handleAddTests}>
                <Plus className='h-4 w-4' />
                Add Framework
              </Button>
            )}
          </div>

          {/* Framework cards */}
          {frameworks.length === 0 && !frameworksQuery.isLoading ? (
            <Card className='p-12'>
              <div className='text-center'>
                <Shield className='text-muted-foreground/50 mx-auto mb-4 h-12 w-12' />
                <h3 className='mb-2 text-lg font-medium'>No frameworks yet</h3>
                <p className='text-muted-foreground mb-4'>
                  Get started by selecting a compliance framework and apps.
                </p>
                {!isViewer && (<Button size='sm' onClick={handleAddTests}>
                  <Plus className=' h-4 w-4' />
                  Add Framework
                </Button>)}
              </div>
            </Card>
          ) : (
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              {frameworks.map((framework) => {
                const isCustom = !!framework.workspaceId
                const stats = frameworkStats[framework.id] || {
                  totalControls: 0,
                  totalTests: 0,
                  passed: 0,
                  failed: 0,
                  inReview: 0,
                  notStarted: 0,
                }

                return (
                  <FrameworkCard
                    key={framework.id}
                    id={framework.id}
                    name={framework.name}
                    isCustom={isCustom}
                    stats={stats}
                    onClick={() => handleFrameworkClick(framework.id)}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create Framework Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false)
            resetForm()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Custom Framework</DialogTitle>
            <DialogDescription>
              Create a new compliance framework for your workspace.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='name'>Framework Name *</Label>
              <Input
                id='name'
                placeholder='e.g., Custom Security Framework'
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='slug'>Slug *</Label>
              <Input
                id='slug'
                placeholder='e.g., custom-security'
                value={formData.slug}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    slug: e.target.value.toLowerCase().replace(/\s+/g, '-'),
                  })
                }
              />
              <p className='text-muted-foreground mt-1 text-xs'>
                Unique identifier (lowercase, hyphens only)
              </p>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                placeholder='Describe the purpose of this framework...'
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setIsCreateDialogOpen(false)
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFramework}
              disabled={createFrameworkMutation.isPending}
            >
              {createFrameworkMutation.isPending
                ? 'Creating...'
                : 'Create Framework'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/compliance/'
)({
  component: ComplianceHomePage,
})
